import { useEffect, useMemo, useState } from "react";
import { Check, Copy, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Badge, PanelTitle, StatCard, fmtDate } from "./ui";

const VIG = { vigente: ["activa", "Vigente"], vencido: ["rechazado", "Vencido"], baja: ["rechazado", "Dado de baja"] };

const CodeChip = ({ c }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(c.code);
    setCopied(true);
    toast.success(`Código ${c.code} copiado`);
    setTimeout(() => setCopied(false), 1500);
  };
  const active = c.vigencia === "vigente";
  return (
    <li className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${active ? "border-ink/10 bg-white" : "border-ink/10 bg-paper/60 opacity-70"}`} data-testid={`my-code-${c.code}`}>
      <div>
        <p className="font-mono text-base font-semibold tracking-wide">{c.code}</p>
        <p className="text-xs text-ink/60">Enviado {fmtDate(c.sent_at)} · Vence {fmtDate(c.expires_at)}</p>
      </div>
      <div className="flex items-center gap-2">
        <Badge status={VIG[c.vigencia][0]} label={VIG[c.vigencia][1]} />
        {active && (
          <button onClick={copy} className="grid h-8 w-8 place-items-center rounded-full border border-ink/15 hover:bg-paper" title="Copiar" data-testid={`my-code-copy-${c.code}`}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    </li>
  );
};

export const MyCodesPanel = () => {
  const [codes, setCodes] = useState(null);
  useEffect(() => {
    api.get("/me/codes").then(({ data }) => setCodes(data)).catch(() => setCodes([]));
  }, []);
  const groups = useMemo(() => {
    const g = {};
    (codes || []).forEach((c) => {
      if (!g[c.platform]) g[c.platform] = [];
      g[c.platform].push(c);
    });
    return g;
  }, [codes]);
  const vigentes = (codes || []).filter((c) => c.vigencia === "vigente").length;

  return (
    <div data-testid="my-codes-panel">
      <PanelTitle title="Mis códigos de acceso" text="Códigos de acceso gratuito (30 días) que el administrador te ha enviado para que tus prospectos prueben las plataformas. Cada código es de un solo uso." />
      {codes === null ? (
        <Loader2 className="mt-6 h-6 w-6 animate-spin" />
      ) : !codes.length ? (
        <div className="mt-6 grid place-items-center rounded-2xl border border-dashed border-ink/20 bg-white px-6 py-14 text-center" data-testid="my-codes-empty">
          <KeyRound className="h-8 w-8 text-ink/40" />
          <p className="mt-3 text-sm text-ink/70">Todavía no tienes códigos asignados. Solicítalos al administrador por WhatsApp.</p>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-3 gap-3">
            <StatCard dark label="Códigos vigentes" value={vigentes} testId="my-codes-vigentes" />
            <StatCard label="Total recibidos" value={codes.length} testId="my-codes-total" />
            <StatCard label="Plataformas" value={Object.keys(groups).length} testId="my-codes-platforms" />
          </div>
          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            {Object.entries(groups).map(([platform, list]) => (
              <section key={platform} className="rounded-[1.5rem] border border-ink/10 bg-white p-5" data-testid={`my-codes-group-${platform.replace(" ", "-")}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl font-semibold">{platform}</h3>
                  <span className="rounded-full bg-paper px-2.5 py-1 font-mono text-xs">{list.filter((c) => c.vigencia === "vigente").length} vigentes · {list.length} total</span>
                </div>
                <ul className="mt-4 space-y-2">{list.map((c) => <CodeChip key={c.code} c={c} />)}</ul>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
